

import { Dispatch } from 'redux';
import {
  SETChileComponentName,
  SETHasServiceRequestTypeLogData,
  SETLogEventData,
  SETIsCustomeLog
} from '../../../../core/store';


export interface MCAPSCopilotTelemetryData {
  // Standard telemetry properties (matching project pattern - ONLY these 4 fields)
  elementName?: string;
  elementId?: string;
  action?: "click" | "submit"; // Only click or submit to match existing queries
  message?: string;
  
  // Internal properties for message building (not sent to telemetry)
  query?: string;
  response?: string;
  error?: string;
  component?: string;
  suggestion?: string;
  promptPreview?: string;
}

export class MCAPSCopilotTelemetry {
  private static instance: MCAPSCopilotTelemetry;
  private dispatch: Dispatch;
  private isDebugMode: boolean = process.env.NODE_ENV === 'development';

  private constructor(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  public static getInstance(dispatch: Dispatch): MCAPSCopilotTelemetry {
    if (!MCAPSCopilotTelemetry.instance) {
      MCAPSCopilotTelemetry.instance = new MCAPSCopilotTelemetry(dispatch);
    }
    MCAPSCopilotTelemetry.instance.dispatch = dispatch;
    return MCAPSCopilotTelemetry.instance;
  }


  private logTelemetryEvent(eventName: string, eventData: MCAPSCopilotTelemetryData, defaultElementId: string = "") {

    // Use provided values or defaults (following project pattern)
  const elementName = eventData.elementName || `MCAPSHelp Copilot: ${eventName}`;
    const elementId = eventData.elementId || defaultElementId;
    const action = eventData.action || "click"; // Default to click for existing query compatibility
  const message = eventData.message || `User ${eventName} in MCAPSHelp Copilot`;

  // This sets customDimensions.pageName = 'MCAPSHelp Copilot' for KQL queries
  this.dispatch(SETChileComponentName("MCAPSHelp Copilot"));
    this.dispatch(SETHasServiceRequestTypeLogData(true));
    // Send ONLY the 4 standard fields to match existing queries
    this.dispatch(
      SETLogEventData({
        elementName,
        elementId,
        action,
        message
      })
    );
    this.dispatch(SETIsCustomeLog(true));
  }

  /**
   * Log session duration when user leaves or chat unmounts
   */
  public logSessionDuration(durationMinutes: string, conversationId?: string) {
    this.logTelemetryEvent("Session Duration", {
      elementName: "WWIC Copilot: Session Time",
      elementId: conversationId || "copilot-session",
      action: "submit",
      message: `Session duration: ${durationMinutes} minutes`
    });
  }

  /**
  * Log when MCAPSHelp Copilot panel is opened
   */
  public logPanelOpened() {
    this.logTelemetryEvent("Panel Opened", {
  elementName: "MCAPSHelp Copilot: Panel",
      elementId: "mcapshelp-copilot",
      action: "click",
  message: "MCAPSHelp Copilot panel opened"
    });
  }

  /**
  * Log when MCAPSHelp Copilot panel is closed
   */
  public logPanelClosed() {
    this.logTelemetryEvent("Panel Closed", {
  elementName: "MCAPSHelp Copilot: Panel",
      elementId: "mcaps-copilot-panel",
      action: "click",
  message: "MCAPSHelp Copilot panel closed"
    });
  }

  /**
  * Log when MCAPSHelp Copilot is refreshed
   */
  public logPanelRefreshed() {
    this.logTelemetryEvent("Panel Refreshed", {
  elementName: "MCAPSHelp Copilot: Panel",
      elementId: "mcaps-copilot-refresh",
      action: "click",
  message: "MCAPSHelp Copilot panel refreshed"
    });
  }

  /**
   * Log when user submits a query
   */
  public logQuerySubmitted(data: MCAPSCopilotTelemetryData) {
    this.logTelemetryEvent("Query Submitted", {
      elementName: data.elementName || "MCAPSHelp Copilot: Query Submitted",
      elementId: data.elementId || "copilot-chat-session",
      action: "submit",
      message: data.query ? `User query: ${data.query}` : "User submitted a query"
    });
  }

  /**
  * Log when AI response is received
   */
  public logResponseReceived(data: MCAPSCopilotTelemetryData) {
    const responsePreview = data.response ? data.response.substring(0, 100) : "Response received";
    this.logTelemetryEvent("Response Received", {
      elementName: data.elementName || "MCAPSHelp Copilot: AI Response",
      elementId: data.elementId || "copilot-chat-session",
      action: "click",
      message: `AI response: ${responsePreview}${data.response && data.response.length > 100 ? '...' : ''}`
    });
  }

  /**
  * Log when an error occurs
   */
  public logError(data: MCAPSCopilotTelemetryData) {
    this.logTelemetryEvent("Error Occurred", {
      elementName: data.elementName || "MCAPSHelp Copilot: Error",
      elementId: data.elementId || "copilot-error",
      action: "click",
      message: data.error ? `Error: ${data.error}` : "An error occurred in MCAPSHelp Copilot"
    });
  }

  /**
  * Log when user clicks follow-up question
   */
  public logFollowUpClicked(data: MCAPSCopilotTelemetryData) {
    const question = data.query || "";
    this.logTelemetryEvent("Follow-up Clicked", {
  elementName: "MCAPSHelp Copilot: Follow-up Question",
      elementId: "copilot-chat-session",
      action: "click",
      message: question ? `Follow-up question clicked: ${question.substring(0, 100)}${question.length > 100 ? '...' : ''}` : "Follow-up question clicked"
    });
  }

  /**
  * Log when user copies content
   */
  public logContentCopied(data: MCAPSCopilotTelemetryData) {
    this.logTelemetryEvent("Content Copied", {
  elementName: "MCAPSHelp Copilot: Content Copy",
      elementId: "copilot-chat-session",
      action: "click",
      message: "User copied content from MCAPSHelp Copilot"
    });
  }

  /**
  * Log component interaction events (buttons, clicks, etc.)
   */
  public logComponentInteraction(data: MCAPSCopilotTelemetryData) {
    const componentName = data.component || "Component";
    const actionName = data.action || "click";
    this.logTelemetryEvent("Component Interaction", {
  elementName: `MCAPSHelp Copilot: ${componentName}`,
      elementId: `mcaps-copilot-${componentName.toLowerCase()}`,
      action: actionName,
      message: `User ${actionName} on ${componentName}`
    });
  }

  /**
  * Log UI state changes (expand/collapse, modal open/close)
   */
  public logUIStateChange(data: MCAPSCopilotTelemetryData) {
    const component = data.component || "UI";
    const action = data.action || "click";
    this.logTelemetryEvent("UI State Change", {
  elementName: `MCAPSHelp Copilot: ${component} State Change`,
      elementId: `mcaps-copilot-${component.toLowerCase()}-state`,
      action: action,
      message: `${component} ${action} state changed`
    });
  }

  /**
  * Log welcome message and suggestion interactions
   */
  public logWelcomeInteraction(data: MCAPSCopilotTelemetryData) {
    const action = data.action || "click";
    const suggestion = data.suggestion || "";
    this.logTelemetryEvent("Welcome Interaction", {
  elementName: "MCAPSHelp Copilot: Welcome Interaction",
      elementId: "mcaps-copilot-welcome",
      action: action,
      message: suggestion ? `Welcome ${action}: ${suggestion}` : `Welcome ${action}`
    });
  }

  /**
  * Log prompt gallery interactions
   */
  public logPromptGalleryAction(data: MCAPSCopilotTelemetryData) {
    const action = data.action || "click";
    const promptPreview = data.promptPreview || "";
    const message = promptPreview ? 
      `Prompt Gallery ${action}: ${promptPreview.substring(0, 50)}${promptPreview.length > 50 ? '...' : ''}` : 
      `Prompt Gallery ${action}`;
    
    this.logTelemetryEvent("Prompt Gallery Action", {
  elementName: "MCAPSHelp Copilot: Prompt Gallery",
      elementId: "mcaps-copilot-prompt-gallery",
      action: action,
      message: message
    });
  }

  /**
  * Log new conversation actions
   */
  public logNewConversationAction(data: MCAPSCopilotTelemetryData) {
    const action = data.action || "click";
    this.logTelemetryEvent("New Conversation Action", {
  elementName: "MCAPSHelp Copilot: New Conversation",
      elementId: "mcaps-copilot-new-conversation",
      action: action,
      message: `New conversation ${action}`
    });
  }

  /**
  * Log panel expand/collapse actions with size transition
   */
  public logPanelToggle(data: MCAPSCopilotTelemetryData & { fromSize?: string; toSize?: string }) {
    const action = data.action || "click";
    const fromSize = data.fromSize || "unknown";
    const toSize = data.toSize || "unknown";
    
    this.logTelemetryEvent("Panel Toggle", {
      elementName: "MCAPSHelp Copilot: Panel Toggle",
      elementId: `mcaps-copilot-panel-toggle-${fromSize}-to-${toSize}`,
      action: action,
      message: `Copilot panel resized from ${fromSize} to ${toSize}`
    });
  }

  /**
  * Log exit switch toggle interactions
   */
  public logExitSwitchToggle(data: MCAPSCopilotTelemetryData) {
    const action = data.action || "click";
    const isEnabled = data.message?.includes("enabled") || data.message?.includes("on");
    const switchState = isEnabled ? "enabled" : "disabled";
    
    this.logTelemetryEvent("Exit Switch Toggle", {
      elementName: "MCAPSHelp Copilot: Exit Switch",
      elementId: "mcaps-copilot-exit-switch",
      action: action,
      message: data.message || `Exit switch ${switchState}`
    });
  }

  /**
  * Log exit confirmation interactions
   */
  public logExitConfirmation(data: MCAPSCopilotTelemetryData) {
    const action = data.action || "click";
    
    this.logTelemetryEvent("Exit Confirmation", {
      elementName: "MCAPSHelp Copilot: Exit Confirmation",
      elementId: "mcaps-copilot-exit-confirmation",
      action: action,
      message: data.message || `Exit confirmation ${action}`
    });
  }

  /**
   * Generic method to handle custom control telemetry events from web component
   */
  public handleCustomControlTelemetry(event: CustomEvent) {
    const { eventName, eventData } = event.detail;

    switch (eventName) {
      case 'copilot-query-submitted':
        this.logQuerySubmitted(eventData);
        break;
      case 'copilot-response-received':
        this.logResponseReceived(eventData);
        break;
      case 'copilot-error':
        this.logError(eventData);
        break;
      case 'copilot-followup-clicked':
        this.logFollowUpClicked(eventData);
        break;
      case 'copilot-content-copied':
        this.logContentCopied(eventData);
        break;
      default:
        // Handle unknown events with generic logging
        this.logTelemetryEvent(eventName, {
          elementName: "MCAPSHelp Copilot: Unknown Event",
          elementId: "copilot-unknown",
          action: "click",
          message: `Unknown copilot event: ${eventName}`
        });
        break;
    }
  }

  /**
   * Set up event listeners for custom control telemetry
   */
  public setupTelemetryListeners(panelIsOpen: boolean): () => void {
    if (!panelIsOpen) {
      return () => {}; // Return empty cleanup function
    }

    const setupListener = () => {
      const wrapperElement = document.getElementById('copilotForm');
      const innerElement = document.querySelector('copilot-chat-session') as unknown as HTMLElement;

      if (wrapperElement) {
        wrapperElement.addEventListener('copilot-telemetry', this.handleCustomControlTelemetry.bind(this) as EventListener);
      }
      
      if (innerElement) {
        innerElement.addEventListener('copilot-telemetry', this.handleCustomControlTelemetry.bind(this) as EventListener);
      }
      
      if (!wrapperElement && !innerElement) {
        setTimeout(setupListener, 500);
      }

      return { wrapperElement, innerElement };
    };

    // Setup with initial delay
    const timeoutId = setTimeout(setupListener, 100);
    const { wrapperElement, innerElement } = setupListener();

    // Return cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (wrapperElement) {
        wrapperElement.removeEventListener('copilot-telemetry', this.handleCustomControlTelemetry.bind(this) as EventListener);
      }
      if (innerElement) {
        (innerElement as unknown as HTMLElement).removeEventListener('copilot-telemetry', this.handleCustomControlTelemetry.bind(this) as EventListener);
      }
    };
  }
}

/**
 * Hook-style utility for easier usage in React components
 */
export const useMCAPSCopilotTelemetry = (dispatch: Dispatch) => {
  return MCAPSCopilotTelemetry.getInstance(dispatch);
};

/**
 * KQL Query Templates for Application Insights
 * Copy these queries directly into Application Insights Analytics
 */
export const MCAPS_COPILOT_QUERIES = {
  // Your main query - gets ALL MCAPSHelp Copilot events
  ALL_EVENTS: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| project timestamp, customDimensions.elementName, customDimensions.elementId, customDimensions.action, customDimensions.message
| order by timestamp desc`,

  // Query submissions only (action=submit)
  USER_QUERIES: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.action=='submit'
| where customDimensions.elementName=='MCAPSHelp Copilot: Query Submitted'
| project timestamp, customDimensions.elementName, customDimensions.message
| order by timestamp desc`,

  // AI responses (action=click for responses)
  AI_RESPONSES: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: AI Response'
| project timestamp, customDimensions.elementName, customDimensions.message
| order by timestamp desc`,

  // Panel usage analytics (action=click)
  PANEL_USAGE: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Panel'
| where customDimensions.action=='click'
| summarize count() by customDimensions.message, bin(timestamp, 1h)
| render timechart`,

  // Error tracking
  ERRORS: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Error'
| project timestamp, customDimensions.message, customDimensions.elementId
| order by timestamp desc`,

  // User activity summary by action type
  USER_SUMMARY: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| summarize EventCount = count() by customDimensions.action, customDimensions.elementName
| order by EventCount desc`,

  // Component interaction analytics
  COMPONENT_INTERACTIONS: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName startswith 'MCAPSHelp Copilot:'
| where customDimensions.action in ('click', 'submit')
| project timestamp, customDimensions.elementName, customDimensions.action, customDimensions.message
| summarize count() by customDimensions.elementName, bin(timestamp, 1h)
| render timechart`,

  // Follow-up question clicks
  FOLLOWUP_QUESTIONS: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Follow-up Question'
| project timestamp, customDimensions.message
| summarize count() by customDimensions.message
| order by count_ desc`,

  // Welcome interaction analytics
  WELCOME_INTERACTIONS: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Welcome Interaction'
| project timestamp, customDimensions.message
| summarize count() by customDimensions.message
| order by count_ desc`,

  // Prompt gallery usage analytics
  PROMPT_GALLERY_USAGE: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Prompt Gallery'
| project timestamp, customDimensions.action, customDimensions.message
| summarize TotalInteractions = count() by customDimensions.action
| order by TotalInteractions desc`,

  // New conversation analytics
  NEW_CONVERSATION_USAGE: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: New Conversation'
| project timestamp, customDimensions.message
| summarize count() by bin(timestamp, 1h)
| render timechart`,

  // Content copy analytics
  CONTENT_COPY_USAGE: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Content Copy'
| project timestamp, customDimensions.message
| summarize count() by bin(timestamp, 1h)
| render timechart`,

  // Comprehensive user journey analysis
  USER_JOURNEY: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementId != ""
| project timestamp, customDimensions.elementId, customDimensions.action, customDimensions.elementName, customDimensions.message
| order by customDimensions.elementId, timestamp asc`,

  // Most popular queries/prompts
  POPULAR_QUERIES: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.action=='submit'
| where customDimensions.message contains "User query:"
| project timestamp, Query = replace(@"User query: ", "", customDimensions.message)
| summarize count() by Query
| order by count_ desc
| limit 10`,

  // Form submissions analysis
  FORM_SUBMISSIONS: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.message contains "Form submission:"
| project timestamp, FormData = replace(@"Form submission: ", "", customDimensions.message)
| summarize count() by bin(timestamp, 1h)
| render timechart`,

  // ===========================================
  // 🚀 ENHANCED ANALYTICS FOR EXECUTIVE REPORTING
  // ===========================================

  // 🔍 CRITICAL: Complete User Activity Sequence - Shows EVERY user interaction in order
  COMPLETE_USER_SEQUENCE: `customEvents
| where customDimensions.elementName startswith "MCAPSHelp Copilot:"
| project timestamp, 
          Feature = tostring(customDimensions.elementName),
          Action = tostring(customDimensions.action), 
          Details = tostring(customDimensions.message),
          ElementId = tostring(customDimensions.elementId)
| order by timestamp asc
| serialize 
| extend PreviousFeature = prev(Feature), 
         TimeBetweenActions = timestamp - prev(timestamp)
| project timestamp, Feature, Action, Details, PreviousFeature, TimeBetweenActions
| order by timestamp desc`,

  // 🖱️ INTERACTION HEATMAP - Every interaction with frequency
  INTERACTION_HEATMAP: `customEvents
| where customDimensions.elementName startswith "MCAPSHelp Copilot:"
| where customDimensions.action in ("click", "submit")
| project timestamp,
          Component = tostring(customDimensions.elementName),
          ActionType = tostring(customDimensions.action),
          Message = tostring(customDimensions.message)
| summarize TotalInteractions = count(), 
            LastInteraction = max(timestamp), 
            FirstInteraction = min(timestamp),
            UniqueUsers = dcount(tostring(customDimensions.elementId))
            by Component, ActionType
| extend InteractionsPerUser = 1.0 * TotalInteractions / UniqueUsers
| order by TotalInteractions desc`,

  // 👥 USER ADOPTION TRENDS - How many users per day/week/month
  USER_ADOPTION_TRENDS: `customEvents
| where customDimensions.elementName startswith "MCAPSHelp Copilot:"
| summarize DailyActiveUsers = dcount(tostring(customDimensions.elementId)),
            TotalInteractions = count()
            by bin(timestamp, 1d)
| extend WeeklyActiveUsers = prev(DailyActiveUsers, 7),
         GrowthRate = (DailyActiveUsers - prev(DailyActiveUsers)) * 100.0 / prev(DailyActiveUsers)
| project Date = timestamp, DailyActiveUsers, TotalInteractions, GrowthRate
| order by Date desc`,

  // 📊 EXECUTIVE SUMMARY - Key metrics for leadership dashboards
  EXECUTIVE_SUMMARY: `customEvents
| where customDimensions.elementName startswith "MCAPSHelp Copilot:"
| summarize 
    // User Metrics
    TotalUniqueUsers = dcount(tostring(customDimensions.elementId)),
    TotalSessions = countif(customDimensions.elementName == "MCAPSHelp Copilot: Panel" and customDimensions.message contains "opened"),
    
    // Engagement Metrics
    TotalQueries = countif(customDimensions.elementName == "MCAPSHelp Copilot: Query Submitted"),
    TotalResponses = countif(customDimensions.elementName == "MCAPSHelp Copilot: AI Response"),
    FollowUpClicks = countif(customDimensions.elementName == "MCAPSHelp Copilot: Follow-up Question"),
    ContentCopies = countif(customDimensions.elementName == "MCAPSHelp Copilot: Content Copy"),
    
    // Feature Usage
    PromptGalleryActions = countif(customDimensions.elementName == "MCAPSHelp Copilot: Prompt Gallery"),
    NewConversations = countif(customDimensions.elementName == "MCAPSHelp Copilot: New Conversation"),
    
    // Quality Metrics
    TotalErrors = countif(customDimensions.elementName == "MCAPSHelp Copilot: Error")
    
    by bin(timestamp, 1d)
| extend 
    // Calculated KPIs
    QueriesPerSession = 1.0 * TotalQueries / TotalSessions,
    EngagementRate = 100.0 * FollowUpClicks / TotalResponses,
    ErrorRate = 100.0 * TotalErrors / TotalQueries,
    ContentCopyRate = 100.0 * ContentCopies / TotalResponses
| project 
    Date = timestamp, 
    TotalUniqueUsers, 
    TotalSessions, 
    QueriesPerSession, 
    EngagementRate, 
    ErrorRate, 
    ContentCopyRate,
    PromptGalleryActions,
    NewConversations
| order by Date desc`,

  // 🔄 EXIT SWITCH ANALYTICS - Track exit switch usage patterns
  EXIT_SWITCH_USAGE: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Exit Switch'
| project timestamp, customDimensions.action, customDimensions.message
| summarize SwitchToggles = count(), 
            EnabledCount = countif(customDimensions.message contains "enabled"),
            DisabledCount = countif(customDimensions.message contains "disabled")
            by bin(timestamp, 1h)
| extend EnabledRate = 100.0 * EnabledCount / SwitchToggles
| render timechart`,

  // 🚪 EXIT CONFIRMATION ANALYTICS - Track confirmation dialog interactions
  EXIT_CONFIRMATION_USAGE: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName=='MCAPSHelp Copilot: Exit Confirmation'
| project timestamp, customDimensions.message
| summarize TotalConfirmations = count(),
            DialogsShown = countif(customDimensions.message contains "dialog shown"),
            Canceled = countif(customDimensions.message contains "canceled"),
            TurnedOff = countif(customDimensions.message contains "turned off")
            by bin(timestamp, 1h)
| extend CancelRate = 100.0 * Canceled / DialogsShown,
         CompletionRate = 100.0 * TurnedOff / DialogsShown
| render timechart`,

  // 📊 EXIT FEATURE SUMMARY - Complete exit feature analytics
  EXIT_FEATURE_SUMMARY: `customEvents
| where customDimensions.pageName=='MCAPSHelp Copilot'
| where customDimensions.elementName in ('MCAPSHelp Copilot: Exit Switch', 'MCAPSHelp Copilot: Exit Confirmation')
| summarize 
    TotalSwitchInteractions = countif(customDimensions.elementName == "MCAPSHelp Copilot: Exit Switch"),
    SwitchEnabled = countif(customDimensions.elementName == "MCAPSHelp Copilot: Exit Switch" and customDimensions.message contains "enabled"),
    SwitchDisabled = countif(customDimensions.elementName == "MCAPSHelp Copilot: Exit Switch" and customDimensions.message contains "disabled"),
    ConfirmationDialogs = countif(customDimensions.elementName == "MCAPSHelp Copilot: Exit Confirmation" and customDimensions.message contains "dialog shown"),
    ConfirmationsCanceled = countif(customDimensions.elementName == "MCAPSHelp Copilot: Exit Confirmation" and customDimensions.message contains "canceled")
    by bin(timestamp, 1d)
| extend 
    ExitAttemptRate = 100.0 * SwitchEnabled / TotalSwitchInteractions,
    ConfirmationCancelRate = 100.0 * ConfirmationsCanceled / ConfirmationDialogs
| project 
    Date = timestamp,
    TotalSwitchInteractions,
    ExitAttemptRate,
    ConfirmationDialogs,
    ConfirmationCancelRate
| order by Date desc`
};


